import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, User, Car, FileText, CreditCard, Download, Pause, Play, XCircle, ReceiptText, Send, CheckCircle2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import StripeHealthBadge from '../../components/admin/StripeHealthBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('fr-CH');
  } catch { return iso; }
};

const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const { token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const result = await api.getCustomerDetail(token, customerId);
        setData(result);
      } catch (err) {
        toast({
          title: 'Erreur',
          description: err.message,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [customerId, token, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Client introuvable</p>
        <Button onClick={() => navigate('/admin/customers')} className="mt-4">
          Retour à la liste
        </Button>
      </div>
    );
  }

  const { customer, subscriptions, documents, stats, payment_history, invoice_records, invoice_contracts, invoice_attachments } = data;
  const isInvoice = customer?.payment_method === 'invoice';

  const downloadAuthed = async (url, filename) => {
    try {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Échec du téléchargement');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, variant: 'destructive' });
    }
  };

  const handleResendContract = async (recordId) => {
    if (!window.confirm("Renvoyer le contrat par e-mail au client ?")) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/invoice-payments/${recordId}/resend-contract`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || "Échec du renvoi");
      toast({ title: 'Contrat renvoyé', description: 'Le contrat a été renvoyé au client.' });
      // refresh
      const result = await api.getCustomerDetail(token, customerId);
      setData(result);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, variant: 'destructive' });
    }
  };

  const handleDownloadDocument = async (documentId, documentType) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/documents/${documentId}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Échec du téléchargement');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentType === 'invoice' ? 'facture' : 'contrat'}_${documentId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Téléchargement réussi',
        description: 'Le document a été téléchargé.',
      });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Actif', class: 'bg-green-100 text-green-800' },
      paused: { label: 'En pause', class: 'bg-orange-100 text-orange-800' },
      pending: { label: 'En attente', class: 'bg-yellow-100 text-yellow-800' },
      suspended: { label: 'Suspendu', class: 'bg-red-100 text-red-800' },
      cancelled: { label: 'Annulé', class: 'bg-red-100 text-red-800' },
      expired: { label: 'Expiré', class: 'bg-gray-100 text-gray-800' },
      completed: { label: 'Terminé', class: 'bg-gray-100 text-gray-800' },
    };

    const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const handlePauseSubscription = async (subscriptionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir mettre cet abonnement en pause ?')) {
      return;
    }

    setActionLoading(subscriptionId);
    try {
      await api.pauseSubscription(token, subscriptionId);
      toast({
        title: 'Succès',
        description: 'Abonnement mis en pause avec succès',
      });
      
      // Refresh data
      const result = await api.getCustomerDetail(token, customerId);
      setData(result);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeSubscription = async (subscriptionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir réactiver cet abonnement ?')) {
      return;
    }

    setActionLoading(subscriptionId);
    try {
      await api.resumeSubscription(token, subscriptionId);
      toast({
        title: 'Succès',
        description: 'Abonnement réactivé avec succès',
      });
      
      // Refresh data
      const result = await api.getCustomerDetail(token, customerId);
      setData(result);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('⚠️ ATTENTION: Êtes-vous sûr de vouloir annuler définitivement cet abonnement ? Cette action est irréversible.')) {
      return;
    }

    setActionLoading(subscriptionId);
    try {
      await api.cancelSubscription(token, subscriptionId);
      toast({
        title: 'Succès',
        description: 'Abonnement annulé avec succès',
      });
      
      // Refresh data
      const result = await api.getCustomerDetail(token, customerId);
      setData(result);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/customers')}
            className="mb-2 -ml-2"
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour à la liste
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            {customer.first_name} {customer.last_name}
            {isInvoice ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 rounded-full" data-testid="detail-badge-invoice">
                <ReceiptText size={12} /> Facture
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-200 rounded-full" data-testid="detail-badge-card">
                <CreditCard size={12} /> Carte
              </span>
            )}
          </h1>
          <p className="text-gray-600 mt-1">{customer.email}</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Abonnements</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.total_subscriptions ?? subscriptions.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Abonnements Actifs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {stats?.active_subscriptions ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>{isInvoice ? 'Total encaissé' : 'Documents Générés'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {isInvoice
                ? `CHF ${(stats?.total_paid ?? 0).toFixed(2)}`
                : (stats?.total_documents ?? documents.length)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User size={20} />
            Informations Client
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Nom complet</p>
              <p className="font-medium">
                {customer.first_name} {customer.last_name}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{customer.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Téléphone</p>
              <p className="font-medium">{customer.phone}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Adresse</p>
              <p className="font-medium">
                {customer.address}, {customer.zip_code} {customer.city}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard size={20} />
            Abonnements
          </CardTitle>
          <CardDescription>
            {subscriptions.length} abonnement{subscriptions.length > 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Aucun abonnement</p>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((sub) => (
                <div
                  key={sub.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-lg">
                        Abonnement {sub.plan_code}
                      </h4>
                      <p className="text-sm text-gray-600">
                        CHF {sub.monthly_price}/mois • Franchise: CHF {sub.franchise_chf}
                      </p>
                    </div>
                    {getStatusBadge(sub.status)}
                  </div>

                  {/* Stripe Health Badge — silent auto-sync + visible status */}
                  {sub.stripe_subscription_id && (
                    <div className="mb-3" data-testid={`stripe-health-${sub.id}`}>
                      <StripeHealthBadge subscriptionId={sub.id} />
                    </div>
                  )}

                  {/* Vehicle Info */}
                  {sub.vehicle && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Car size={16} className="text-blue-600" />
                        <span className="font-medium text-blue-900">Véhicule</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Marque/Modèle:</span>
                          <p className="font-medium">
                            {sub.vehicle.brand} {sub.vehicle.model || ''}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">Année:</span>
                          <p className="font-medium">{sub.vehicle.year}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Plaque:</span>
                          <p className="font-medium">{sub.vehicle.plate_number}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Kilométrage:</span>
                          <p className="font-medium">{sub.vehicle.mileage} km</p>
                        </div>
                        {sub.vehicle.vin && (
                          <div className="col-span-2">
                            <span className="text-gray-600">VIN:</span>
                            <p className="font-medium font-mono text-xs">
                              {sub.vehicle.vin}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                    <div>
                      <span>Engagement:</span> {sub.commitment_months} mois
                    </div>
                    <div>
                      <span>Créé le:</span>{' '}
                      {new Date(sub.created_at).toLocaleDateString('fr-CH')}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {(sub.status === 'active' || sub.status === 'paused' || sub.status === 'pending') && (
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      {sub.status === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePauseSubscription(sub.id)}
                          disabled={actionLoading === sub.id}
                          className="flex items-center gap-2 text-orange-600 border-orange-300 hover:bg-orange-50"
                        >
                          <Pause size={16} />
                          {actionLoading === sub.id ? 'Traitement...' : 'Mettre en pause'}
                        </Button>
                      )}
                      
                      {sub.status === 'paused' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResumeSubscription(sub.id)}
                          disabled={actionLoading === sub.id}
                          className="flex items-center gap-2 text-green-600 border-green-300 hover:bg-green-50"
                        >
                          <Play size={16} />
                          {actionLoading === sub.id ? 'Traitement...' : 'Reprendre'}
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCancelSubscription(sub.id)}
                        disabled={actionLoading === sub.id}
                        className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <XCircle size={16} />
                        {actionLoading === sub.id ? 'Traitement...' : 'Annuler'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents */}
      {!isInvoice && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText size={20} />
            Documents
          </CardTitle>
          <CardDescription>
            {(documents || []).length} document{(documents || []).length > 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(documents || []).length === 0 ? (
            <p className="text-gray-500 text-center py-4">Aucun document</p>
          ) : (
            <div className="space-y-2">
              {(documents || []).map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div>
                    <p className="font-medium">
                      {doc.type === 'invoice' ? '📄 Facture' : '📋 Contrat'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(doc.created_at).toLocaleDateString('fr-CH')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.file_path ? (
                      <Button
                        size="sm"
                        onClick={() => handleDownloadDocument(doc.id, doc.type)}
                        className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                      >
                        <Download size={16} />
                        Télécharger
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400">En attente</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Invoice (facture) section */}
      {isInvoice && (
        <Card data-testid="invoice-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ReceiptText size={20} className="text-amber-600" />
              Paiements par facture
            </CardTitle>
            <CardDescription>
              Historique des dossiers facture, contrat, paiements et factures envoyées.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Contrats générés */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FileText size={14} /> Contrats
              </p>
              {(invoice_contracts || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic">Aucun contrat généré pour ce client. Confirmez le 1er paiement pour générer automatiquement le contrat.</p>
              ) : (
                <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                  {(invoice_contracts || []).map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`contract-${c.id}`}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#52a675]" />
                        <span className="font-medium">{c.filename || 'Contrat'}</span>
                        <span className="text-xs text-gray-500">· généré le {fmtDateTime(c.generated_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadAuthed(
                            `${API_URL}/api/admin/invoice-payments/${c.record_id}/contract/${c.id}`,
                            c.filename || 'contrat.pdf'
                          )}
                          data-testid={`download-contract-${c.id}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> Télécharger
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleResendContract(c.record_id)}
                          className="bg-[#52a675] hover:bg-[#3d8a5c] text-white"
                          data-testid={`resend-contract-${c.record_id}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1" /> Renvoyer
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Factures envoyées */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Send size={14} /> Factures envoyées
              </p>
              {(invoice_attachments || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic">Aucune facture envoyée pour le moment.</p>
              ) : (
                <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                  {(invoice_attachments || []).map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-amber-600" />
                        <span className="font-medium truncate max-w-[260px]" title={a.filename}>{a.filename || 'Facture'}</span>
                        <span className="text-xs text-gray-500">· envoyée le {fmtDateTime(a.uploaded_at)}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadAuthed(
                          `${API_URL}/api/admin/invoice-payments/${a.record_id}/attachment/${a.id}`,
                          a.filename || 'facture.pdf'
                        )}
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> Télécharger
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Paiements */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <CheckCircle2 size={14} /> Paiements enregistrés
              </p>
              {(payment_history || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic">Aucun paiement encore enregistré.</p>
              ) : (
                <table className="w-full text-sm" data-testid="payment-history-table">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100">
                      <th className="text-left py-2">Date</th>
                      <th className="text-left py-2">Type</th>
                      <th className="text-left py-2">Montant</th>
                      <th className="text-left py-2">Référence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(payment_history || []).map((p) => (
                      <tr key={p.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-2">{fmtDate(p.received_at)}</td>
                        <td className="py-2">
                          {p.type === 'first' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-[#52a675]/10 text-[#3d8a5c] border border-[#52a675]/20 rounded-full">
                              <CheckCircle2 className="w-3 h-3" /> 1<sup>er</sup> paiement
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-gray-100 text-gray-700 border border-gray-200 rounded-full">
                              Récurrent
                            </span>
                          )}
                        </td>
                        <td className="py-2 font-medium">{p.amount_chf != null ? `CHF ${Number(p.amount_chf).toFixed(2)}` : '-'}</td>
                        <td className="py-2 text-gray-600 font-mono text-xs">{p.payment_reference || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomerDetailPage;
